/*
  Carpetivity — Bosta delivery address (cart drawer + cart page)
  - Governorate -> Area -> District dropdowns use Bosta's own list
    (https://app.bosta.co/api/v2/cities/getAllDistricts), cached for 24h.
  - Answers are saved to the cart as attributes, so they appear on the order
    under "Additional details" in Shopify admin.
  - Checkout is blocked until required fields are filled, then the Shopify
    checkout address is pre-filled from the answers.
  - If Bosta's list can't load, Area/District become normal text boxes.
*/
(function () {
  'use strict';

  var BOSTA_URL = 'https://app.bosta.co/api/v2/cities/getAllDistricts';
  var CACHE_KEY = 'cvBostaAreas_v1';
  var CACHE_TTL = 24 * 60 * 60 * 1000;

  // Order of this list = order the fields appear on the Shopify order
  var FIELDS = [
    ['full', 'Delivery - Full address'],
    ['gov', 'Delivery - Governorate'],
    ['area', 'Delivery - Area'],
    ['district', 'Delivery - District'],
    ['street', 'Delivery - Street'],
    ['building', 'Delivery - Building'],
    ['floor', 'Delivery - Floor'],
    ['apartment', 'Delivery - Apartment'],
    ['landmark', 'Delivery - Landmark']
  ];
  // Older keys we no longer want on orders
  var OLD_KEYS = ['_bosta_city_id', '_bosta_district_id'];
  var DRAFT_KEY = 'cvDeliveryDraft_v1';

  // Bosta city name -> Shopify checkout governorate code (Egypt)
  var PROVINCE = {
    'Alexandria': 'ALX', 'Assuit': 'AST', 'Aswan': 'ASN', 'Bani Suif': 'BNS',
    'Behira': 'BH', 'Cairo': 'C', 'Dakahlia': 'DK', 'Damietta': 'DT',
    'El Kalioubia': 'KB', 'Fayoum': 'FYM', 'Gharbia': 'GH', 'Giza': 'GZ',
    'Ismailia': 'IS', 'Kafr Alsheikh': 'KFS', 'Luxor': 'LX', 'Matrouh': 'MT',
    'Menya': 'MN', 'Monufia': 'MNF', 'New Valley': 'WAD', 'North Coast': 'MT',
    'North Sinai': 'SIN', 'Port Said': 'PTS', 'Qena': 'KN', 'Red Sea': 'BA',
    'Sharqia': 'SHR', 'Sohag': 'SHG', 'South Sinai': 'JS', 'Suez': 'SUZ'
  };

  // Used only if Bosta's list cannot be loaded
  var FALLBACK_GOVS = Object.keys(PROVINCE).filter(function (n) { return n !== 'North Sinai'; });

  var data = null;          // [{name, ar, id, zones:[{name, ar, districts:[{name, ar, id}]}]}]
  var loadFailed = false;
  var loading = null;
  var state = {};           // current answers, survives drawer refreshes
  var saveTimer = null;
  var savePromise = Promise.resolve();

  // ---------- data ----------
  function readCache() {
    try {
      var raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var c = JSON.parse(raw);
      if (!c || !c.t || Date.now() - c.t > CACHE_TTL) return null;
      return c.d;
    } catch (e) { return null; }
  }
  function writeCache(d) {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), d: d })); } catch (e) {}
  }
  function compact(list) {
    var out = [];
    list.forEach(function (c) {
      var zones = {};
      var order = [];
      (c.districts || []).forEach(function (x) {
        if (!x.dropOffAvailability) return;
        if (!zones[x.zoneName]) { zones[x.zoneName] = { name: x.zoneName, ar: x.zoneOtherName || '', districts: [] }; order.push(x.zoneName); }
        zones[x.zoneName].districts.push({ name: x.districtName, ar: x.districtOtherName || '', id: x.districtId });
      });
      if (!order.length) return;
      var zs = order.map(function (n) { return zones[n]; });
      zs.sort(function (a, b) { return a.name.localeCompare(b.name); });
      zs.forEach(function (z) { z.districts.sort(function (a, b) { return a.name.localeCompare(b.name); }); });
      out.push({ name: c.cityName, ar: c.cityOtherName || '', id: c.cityId, zones: zs });
    });
    out.sort(function (a, b) {
      // Cairo & Giza first (most orders), then alphabetical
      var pri = { 'Cairo': 0, 'Giza': 1 };
      var pa = pri[a.name] !== undefined ? pri[a.name] : 9, pb = pri[b.name] !== undefined ? pri[b.name] : 9;
      return pa - pb || a.name.localeCompare(b.name);
    });
    return out;
  }
  function loadData() {
    if (data || loadFailed) return Promise.resolve();
    if (loading) return loading;
    var cached = readCache();
    if (cached) { data = cached; return Promise.resolve(); }
    loading = fetch(BOSTA_URL)
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (j) {
        var list = (j && j.data) || [];
        if (!list.length) throw new Error('empty');
        data = compact(list);
        writeCache(data);
      })
      .catch(function (e) { loadFailed = true; console.warn('Bosta areas unavailable, using text fields', e); });
    return loading;
  }

  // ---------- helpers ----------
  function label(item) { return item.ar ? item.name + ' — ' + item.ar : item.name; }
  function findGov(name) { return (data || []).filter(function (c) { return c.name === name; })[0]; }
  function findZone(gov, name) { return gov ? gov.zones.filter(function (z) { return z.name === name; })[0] : null; }
  function findDistrict(zone, name) { return zone ? zone.districts.filter(function (d) { return d.name === name; })[0] : null; }
  function q(root, f) { return root.querySelector('[data-cv-field="' + f + '"]'); }

  // Search box above long lists (Area / District). Matches English or Arabic,
  // ignoring spaces, punctuation and common Arabic letter variants.
  function norm(s) {
    return (s || '').toLowerCase()
      .replace(/[أإآ]/g, 'ا').replace(/ة/g, 'ه').replace(/ى/g, 'ي')
      .replace(/[^a-z0-9؀-ۿ]/g, '');
  }
  function ensureFilter(sel, items) {
    var wrap = sel.parentNode;
    var box = wrap.querySelector('.cv-filter');
    var needed = items.length > 12;
    if (!needed) { if (box) box.hidden = true; return; }
    if (!box) {
      box = document.createElement('input');
      box.type = 'search';
      box.className = 'form-control cv-filter';
      box.placeholder = sel.getAttribute('data-cv-field') === 'district'
        ? 'Type to search district or compound — ابحث'
        : 'Type to search area — ابحث';
      box.autocomplete = 'off';
      wrap.insertBefore(box, sel);
      box.addEventListener('input', function (e) {
        e.stopPropagation();
        var t = norm(box.value);
        var list = sel.__items || [];
        var current = state[sel.getAttribute('data-cv-field')];
        var matches = t ? list.filter(function (it) { return norm(it.name).indexOf(t) > -1 || norm(it.ar).indexOf(t) > -1 || it.name === current; }) : list;
        fillSelect(sel, matches, sel.dataset.placeholder + (t ? ' (' + matches.length + ')' : ''), current, true);
      });
    }
    box.hidden = false;
    box.value = '';
  }

  function fillSelect(sel, items, placeholder, selected, keepItems) {
    if (!keepItems) sel.__items = items;
    sel.innerHTML = '';
    var ph = document.createElement('option');
    ph.value = ''; ph.textContent = placeholder;
    sel.appendChild(ph);
    items.forEach(function (it) {
      var o = document.createElement('option');
      o.value = it.name; o.textContent = label(it);
      if (it.name === selected) o.selected = true;
      sel.appendChild(o);
    });
  }

  // Replace a <select> with a text box (fallback when Bosta's list is unavailable)
  function toText(root, f, placeholder) {
    var sel = q(root, f);
    if (!sel || sel.tagName !== 'SELECT') return;
    var inp = document.createElement('input');
    inp.type = 'text'; inp.className = 'form-control'; inp.setAttribute('data-cv-field', f);
    inp.id = sel.id; inp.placeholder = placeholder; inp.value = state[f] || '';
    inp.autocomplete = 'off';
    sel.parentNode.replaceChild(inp, sel);
  }

  // ---------- render ----------
  function render(root) {
    var gSel = q(root, 'gov'), aSel = q(root, 'area'), dSel = q(root, 'district');
    var dWrap = root.querySelector('[data-cv-district-wrap]');

    if (!data) {
      if (gSel && gSel.tagName === 'SELECT') {
        fillSelect(gSel, FALLBACK_GOVS.map(function (n) { return { name: n, ar: '' }; }), gSel.dataset.placeholder, state.gov);
      }
      toText(root, 'area', 'e.g. New Cairo, Maadi, Sheikh Zayed');
      if (dWrap) dWrap.hidden = true;
      bind(root);
      return;
    }

    fillSelect(gSel, data, gSel.dataset.placeholder, state.gov);
    var gov = findGov(state.gov);
    aSel.disabled = !gov;
    fillSelect(aSel, gov ? gov.zones : [], aSel.dataset.placeholder, state.area);
    ensureFilter(aSel, gov && !state.area ? gov.zones : []);

    var zone = findZone(gov, state.area);
    var districts = zone ? zone.districts : [];
    // One district with the same name as the area: no need to ask again
    var single = districts.length === 1;
    if (single) { state.district = districts[0].name; state.districtId = districts[0].id; }
    dWrap.hidden = !zone || single;
    dSel.disabled = !zone;
    fillSelect(dSel, districts, dSel.dataset.placeholder, state.district);
    ensureFilter(dSel, zone && !single && !state.district ? districts : []);
    bind(root);
  }

  function bind(root) {
    if (root.__cvBound) return;
    root.__cvBound = true;
    root.addEventListener('change', onInput);
    root.addEventListener('input', function (e) {
      if (e.target.tagName === 'INPUT') onInput(e);
    });
  }

  function onInput(e) {
    var el = e.target;
    var f = el.getAttribute('data-cv-field');
    if (!f) return;
    var root = el.closest('[data-cv-delivery]');
    state[f] = el.value.trim();
    el.classList.remove('is-invalid');

    if (el.tagName === 'SELECT') {
      if (f === 'gov') {
        state.area = ''; state.district = ''; state.districtId = '';
        var g = findGov(state.gov); state.cityId = g ? g.id : '';
        render(root);
      } else if (f === 'area') {
        state.district = ''; state.districtId = '';
        render(root);
      } else if (f === 'district') {
        var d = findDistrict(findZone(findGov(state.gov), state.area), state.district);
        state.districtId = d ? d.id : '';
      }
    }
    // keep every copy of the form (drawer + page) in sync
    syncOthers(root);
    scheduleSave();
    var err = root.querySelector('[data-cv-error]');
    if (err) err.hidden = true;
  }

  function syncOthers(source) {
    document.querySelectorAll('[data-cv-delivery]').forEach(function (r) {
      if (r === source) return;
      ['street', 'building', 'floor', 'apartment', 'landmark'].forEach(function (f) {
        var el = q(r, f); if (el && el.value !== (state[f] || '')) el.value = state[f] || '';
      });
      render(r);
    });
  }

  // ---------- draft (browser only, while typing) ----------
  var DRAFT_FIELDS = ['gov', 'area', 'district', 'street', 'building', 'floor', 'apartment', 'landmark'];
  function saveDraft() {
    try {
      var d = {};
      DRAFT_FIELDS.forEach(function (f) { if (state[f]) d[f] = state[f]; });
      localStorage.setItem(DRAFT_KEY, JSON.stringify(d));
    } catch (e) {}
  }
  function readDraft() {
    try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null'); } catch (e) { return null; }
  }
  function scheduleSave() {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(saveDraft, 300);
  }

  // ---------- write to the order (on Checkout) ----------
  function fullAddress() {
    var place = [];
    if (state.district && state.district !== state.area) place.push(state.district);
    if (state.area) place.push(state.area);
    if (state.gov) place.push(state.gov);
    var unit = [];
    if (state.building) unit.push('Building ' + state.building);
    if (state.floor) unit.push('Floor ' + state.floor);
    if (state.apartment) unit.push('Apt ' + state.apartment);
    var parts = [place.join(', ')];
    if (state.street) parts.push(state.street);
    if (unit.length) parts.push(unit.join(', '));
    if (state.landmark) parts.push('Landmark: ' + state.landmark);
    return parts.filter(Boolean).join(' — ');
  }
  function post(attrs) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).catch(function () {});
  }
  // Shopify lists attributes in the order they were first added, so clear
  // ours first, then add them back in the fixed FIELDS order.
  function flushSave() {
    clearTimeout(saveTimer);
    saveDraft();
    state.full = fullAddress();
    var clear = {};
    FIELDS.forEach(function (f) { clear[f[1]] = ''; });
    OLD_KEYS.forEach(function (k) { clear[k] = ''; });
    var set = {};
    FIELDS.forEach(function (f) { if (state[f[0]]) set[f[1]] = state[f[0]]; });
    savePromise = savePromise.then(function () { return post(clear); }).then(function () { return post(set); });
    return savePromise;
  }

  // ---------- validation + checkout ----------
  function validate(root) {
    var missing = [];
    function need(f) {
      var el = q(root, f);
      if (!el) return;
      var wrap = el.closest('[data-cv-district-wrap]');
      if (wrap && wrap.hidden) return;
      if (!(state[f] || '').trim()) { el.classList.add('is-invalid'); missing.push(el); }
    }
    ['gov', 'area', 'district', 'street', 'building'].forEach(need);
    return missing;
  }

  function checkoutUrl() {
    var p = [];
    function add(k, v) { if (v) p.push('checkout[shipping_address][' + k + ']=' + encodeURIComponent(v)); }
    add('country', 'EG');
    add('province', PROVINCE[state.gov] || '');
    var area = state.district && state.district !== state.area ? state.area + ' - ' + state.district : (state.area || state.district);
    add('city', area);
    add('address1', state.street);
    var line2 = [];
    if (state.building) line2.push('Building ' + state.building);
    if (state.floor) line2.push('Floor ' + state.floor);
    if (state.apartment) line2.push('Apt ' + state.apartment);
    if (state.landmark) line2.push('Near ' + state.landmark);
    add('address2', line2.join(', '));
    return '/checkout?' + p.join('&');
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.btn-checkout');
    if (!btn) return;
    var root = document.querySelector('#offcanvas-cart.show [data-cv-delivery]') ||
               document.querySelector('#cart [data-cv-delivery]') ||
               document.querySelector('#offcanvas-cart [data-cv-delivery]');
    if (!root) return; // empty cart — nothing to check

    e.preventDefault();
    e.stopPropagation();

    var missing = validate(root);
    if (missing.length) {
      var err = root.querySelector('[data-cv-error]');
      if (err) err.hidden = false;
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(function () { try { missing[0].focus({ preventScroll: true }); } catch (x) {} }, 400);
      return;
    }
    if (typeof window.onClickCheckoutBtn === 'function') window.onClickCheckoutBtn(btn);
    flushSave().then(function () { window.location.href = checkoutUrl(); });
  }, true);

  // ---------- init ----------
  function readSaved(root) {
    // Values already on the cart (rendered by Liquid) seed the state once
    if (state.__seeded) return;
    var draft = readDraft();
    var s = root.dataset;
    DRAFT_FIELDS.forEach(function (f) {
      var v = (draft && draft[f]) || s['saved' + f.charAt(0).toUpperCase() + f.slice(1)];
      if (v) state[f] = v;
    });
    state.__seeded = true;
  }

  // Place a copy of the form in the cart drawer (under the items) and on the cart page.
  // The drawer rebuilds its HTML after every cart change, so this runs again each time.
  var uidCounter = 0;
  function makeForm() {
    var tpl = document.getElementById('cv-delivery-template');
    if (!tpl) return null;
    var html = tpl.innerHTML.replace(/__UID__/g, 'cv' + (++uidCounter));
    var box = document.createElement('div');
    box.innerHTML = html.trim();
    return box.firstElementChild;
  }
  function placeForms() {
    var drawerList = document.querySelector('#offcanvas-cart .offcanvas-body .product-list');
    if (drawerList && !drawerList.parentNode.querySelector('[data-cv-delivery]')) {
      var f1 = makeForm();
      if (f1) drawerList.parentNode.insertBefore(f1, drawerList.nextSibling);
    }
    var summary = document.querySelector('#cart #cart-summary .card-body');
    if (summary && !summary.querySelector('[data-cv-delivery]')) {
      var f2 = makeForm();
      if (f2) summary.insertBefore(f2, summary.firstChild);
    }
  }

  function initAll() {
    placeForms();
    var roots = document.querySelectorAll('[data-cv-delivery]');
    if (!roots.length) return;
    roots.forEach(function (root) {
      readSaved(root);
      ['street', 'building', 'floor', 'apartment', 'landmark'].forEach(function (f) {
        var el = q(root, f); if (el && state[f] !== undefined) el.value = state[f];
      });
    });
    loadData().then(function () {
      document.querySelectorAll('[data-cv-delivery]').forEach(function (root) {
        root.classList.remove('is-loading');
        render(root);
      });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  window.addEventListener('updated.ecomify.cart', initAll);
  document.addEventListener('show.bs.offcanvas', function (e) { if (e.target && e.target.id === 'offcanvas-cart') initAll(); });
})();
